import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

/**
 * Credit requests — the customer tells us what to do with an unapplied payment
 * or credit memo; a person on our team does it in QuickBooks. Nothing here
 * moves money. See netlify/functions/customer-credit-requests.ts.
 */

export type CreditRequestType = 'apply' | 'refund_check';

export interface CreditRequest {
  id: string;
  source_kind: 'payment' | 'credit_memo';
  source_id: string;
  source_reference: string | null;
  credit_at_request: number | string;
  request_type: CreditRequestType;
  apply_oldest: boolean;
  invoices: Array<{ qbo_invoice_id: string; doc_number: string | null; amount: number }> | null;
  requested_amount: number | string;
  mail_to: string | null;
  notes: string | null;
  requested_by_name: string | null;
  requested_by_email: string | null;
  status: 'open' | 'done' | 'cancelled';
  email_error: string | null;
  resolved_at: string | null;
  resolved_reason: string | null;
  created_at: string;
  store_name: string | null;
  // admin only
  customer_name?: string | null;
  customer_id?: string;
  emailed_to?: string[] | null;
  resolved_by?: string | null;
}

export interface RequestOpenInvoice {
  qboInvoiceId: string;
  docNumber: string | null;
  txnDate: string | null;
  balance: number;
  storeName: string | null;
}

export function useCreditRequests() {
  return useQuery({
    queryKey: ['credit-requests'],
    queryFn: () =>
      adminFetch<{ ok: true; requests: CreditRequest[]; open_invoices: RequestOpenInvoice[]; is_group: boolean }>(
        'customer-credit-requests',
      ),
    staleTime: 30 * 1000,
  });
}

export interface CreateCreditRequest {
  source_kind: 'payment' | 'credit_memo';
  source_id: string;
  request_type: CreditRequestType;
  apply_oldest?: boolean;
  invoices?: Array<{ qbo_invoice_id: string; amount: number }>;
  amount?: number | null;
  mail_to?: string | null;
  notes?: string | null;
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['credit-requests'] });
    void qc.invalidateQueries({ queryKey: ['admin-credit-requests'] });
  };
}

export function useCreateCreditRequest() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateCreditRequest) =>
      adminFetch<{ ok: true; request: CreditRequest; emailed: boolean }>('customer-credit-requests', {
        method: 'POST',
        body: JSON.stringify({ action: 'create', ...body }),
      }),
    onSuccess: done,
  });
}

export function useWithdrawCreditRequest() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      adminFetch('customer-credit-requests', { method: 'POST', body: JSON.stringify({ action: 'cancel', id }) }),
    onSuccess: done,
  });
}

/** Staff queue. */
export function useAdminCreditRequests(status: 'open' | 'done' | 'cancelled' | 'all') {
  return useQuery({
    queryKey: ['admin-credit-requests', status],
    queryFn: () =>
      adminFetch<{ ok: true; requests: CreditRequest[]; auto_closed: number }>(
        `admin-credit-requests?status=${status}`,
      ),
    staleTime: 30 * 1000,
  });
}

export function useResolveCreditRequest() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; action: 'done' | 'cancel'; note?: string }) =>
      adminFetch('admin-credit-requests', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: done,
  });
}
